import React, { useMemo } from 'react';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import { useS3Service } from '../../hooks/useS3Service';
import { useS3Filter } from '../../contexts/S3FilterContext';

/**
 * Props for YearFilterComponent
 */
export interface YearFilterComponentProps {
  disabled?: boolean;
  loading?: boolean;
  basePath?: string;
}

/**
 * YearFilterComponent - Single-select filter for S3 file years
 *
 * Displays available years extracted from S3 folder structure.
 */
export const YearFilterComponent: React.FC<YearFilterComponentProps> = ({
  disabled = false,
  loading = false,
  basePath = '',
}) => {
  const { filters, setSelectedYear } = useS3Filter();
  const { s3Service, isAuthenticated } = useS3Service();
  const [years, setYears] = React.useState<string[]>([]);
  const [loadingYears, setLoadingYears] = React.useState(false);

  /**
   * Extract unique years from S3 folder structure
   * Looks for numeric folder names under the selected partner folder
   */
  React.useEffect(() => {
    const extractYears = async () => {
      try {
        setLoadingYears(true);
        if (!s3Service || !isAuthenticated) {
          setYears([]);
          return;
        }

        // Build query path: basePath + partner/ (if selected)
        let queryPath = basePath;
        if (filters.selectedPartners.length === 1) {
          queryPath += `${filters.selectedPartners[0]}/`;
        }

        console.log('[YearFilter] Querying path:', queryPath);
        const result = await s3Service.listObjects(queryPath);

        // Extract unique years from first-level folders
        const yearSet = new Set<string>();

        // Filter for folders only
        const folders = result.items.filter((item) => item.type === 'folder');
        
        console.log('[YearFilter] Found folders:', folders.length);
        
        folders.forEach((folder) => {
          // Extract folder name from key (remove queryPath and trailing slash)
          let folderName = folder.key;
          if (queryPath && folderName.startsWith(queryPath)) {
            folderName = folderName.substring(queryPath.length);
          }
          folderName = folderName.replace(/\/$/, '').split('/')[0];
            
          console.log('[YearFilter] Checking folder:', folder.key, '-> folderName:', folderName);
          
          // Check if folder name is a 4-digit year
          if (folderName && /^\d{4}$/.test(folderName)) {
            console.log('[YearFilter] Adding year:', folderName);
            yearSet.add(folderName);
          }
        });

        // Sort years in descending order (newest first)
        const yearArray = Array.from(yearSet).sort().reverse();
        console.log('[YearFilter] Final years:', yearArray);
        setYears(yearArray);
      } catch (error) {
        console.warn('Failed to extract years from S3:', error);
        setYears([]);
      } finally {
        setLoadingYears(false);
      }
    };

    extractYears();
  }, [s3Service, isAuthenticated, basePath, filters.selectedPartners]);

  return (
    <FormControl fullWidth disabled={disabled || loadingYears || loading}>
      <InputLabel id="year-select-label">Year</InputLabel>
      <Select
        labelId="year-select-label"
        id="year-select"
        value={filters.selectedYear || ''}
        label="Year"
        onChange={(e) => {
          const value = e.target.value === '' ? null : e.target.value;
          setSelectedYear(value);
        }}
      >
        <MenuItem value="">
          <em>All Years</em>
        </MenuItem>
        {years.map((year) => (
          <MenuItem key={year} value={year}>
            {year}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default YearFilterComponent;
